/* Shell frames (MAR-372 hardening) — the shell.css containers whose classes the app shell
   was composing by hand: the `.mv-app` grid, the `.mv-dock` column (overlays.md) and, since
   review round 1, every part of the Ask dock — its head, pin banner, resize grip, thread
   history rows and foot. States are props; the shell composes these and writes no dock class.
   Appended to the library through `export * from './Shell'` in ./index.jsx. */
import * as React from 'react';
import { Card, Eyebrow, Orb } from './index.jsx';

const cx = (...a) => a.filter(Boolean).join(' ');

/** The app grid: rail · topbar · canvas (· context panel) (· dock track). `dock` reserves the
 *  dock's grid track — false below 900px, where the dock floats instead. */
function AppGrid({ ctx = false, dock = true, children, className = '', ...rest }) {
  return <div {...rest} className={cx('mv-app', ctx && 'with-ctx', dock && 'with-dock', className)}>{children}</div>;
}

/** The Ask dock's column. `overlay`: floating (< 900px). `behind`: a drawer is open in the
 *  overlay band, so the collapsed rail stands down. `data-mv-beside-overlays` marks it as the
 *  one region a non-modal Drawer leaves reachable (overlays.md) — see useOverlay. */
const DockPanel = React.forwardRef(function DockPanel({ collapsed = false, overlay = false, behind = false, children, className = '', ...rest }, ref) {
  return (
    <aside ref={ref} {...rest} data-mv-beside-overlays=""
      className={cx('mv-dock', collapsed && 'mv-dock--collapsed', overlay && 'mv-dock--overlay', behind && 'mv-dock--behind', className)}>
      {children}
    </aside>
  );
});

/** The collapsed rail's vertical label. */
function DockRailLabel({ children }) {
  return <span className="mv-dock-label">{children}</span>;
}

/** The resize handle straddling the dock's left border. The drag itself is the caller's
 *  (pointer events: `onPointerDown`). */
function DockGrip({ title = 'Drag to resize', onPointerDown, ...rest }) {
  return <div {...rest} className="mv-dock-grip" title={title} onPointerDown={onPointerDown} />;
}

/** The dock's head, on the topbar's line: Marva's mark, a title, the context line, and the
 *  head's actions (IconButtons) as children. */
function DockHead({ title, context, children }) {
  return (
    <header className="mv-dock-head">
      <Orb size={19} />
      <span className="mv-dock-id">
        <span className="mv-dock-title">{title}</span>
        {context != null && <span className="mv-dock-context">{context}</span>}
      </span>
      {children}
    </header>
  );
}

/** §3.2's pin: what she is reading with you — a kind (eyebrow) and the record's label. */
function DockPin({ kind, label }) {
  return (
    <div className="mv-dock-pin">
      <Orb size={13} />
      <span className="mv-dock-pin-text">
        <Eyebrow as="span">{kind}</Eyebrow>
        <span className="mv-dock-pin-label">{label}</span>
      </span>
    </div>
  );
}

/** The thread history view: a scrolling column of DockThreadRows (or a state). */
function DockHistory({ children }) {
  return <div className="mv-dock-hist">{children}</div>;
}

/** One thread in the history: its title (one line, full on hover) and when it moved. */
function DockThreadRow({ title, time, current = false, onClick }) {
  return (
    <Card interactive padding="sm" tone={current ? 'gold' : 'plain'} aria-current={current ? 'true' : undefined} onClick={onClick}>
      <span className="mv-dock-thread-title" title={typeof title === 'string' ? title : undefined}>{title}</span>
      <span className="mv-dock-thread-time">{time}</span>
    </Card>
  );
}

/** The dock's foot, below both views: the composer. */
function DockFoot({ children }) {
  return <div className="mv-dock-foot">{children}</div>;
}

export { AppGrid, DockPanel, DockRailLabel, DockGrip, DockHead, DockPin, DockHistory, DockThreadRow, DockFoot };
